import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import { AppUseSelector } from '../../redux/hooks'
import { reviewLanguageOverride } from '../../i18n/highlight'
import { LanguageCode, toSupportedLanguage } from '../../i18n/languages'
import {
  languageNameInSentence,
  languageNoticeAnswer,
  readStoredCredentials,
  shouldShowLanguageNotice,
} from '../../i18n/languageNotice'
import { answerLanguageNotice } from '../../i18n/profileLanguage'
import './languageNotice.css'

// Always in English, whatever the interface language, so someone who cannot read the new
// language can still find the way back. Not a translation key on purpose.
const SWITCH_TO_ENGLISH = 'Switch to English'

const track = (event: string, properties: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties)
  } catch {
    /* analytics must never break the page */
  }
}

// Once per page load and language, however many pages the learner opens with it showing.
const shownThisSession = new Set<string>()

/** Test hook. */
export const resetLanguageNoticeTracking = () => shownThisSession.clear()

/**
 * One line above the header: "maayot is now in <language>.", for an existing learner whose
 * interface switched to their browser's language on its own (i18n/languageNotice.ts decides who).
 * Switch to English, Keep, or close all save the language as chosen, so it shows once.
 */
const LanguageNotice: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const credentials = AppUseSelector(({ user }) => user.credentials)
  // Before the profile arrives, the learner as the last visit stored them, so the notice is in the
  // first paint rather than pushing the header down a moment later.
  const stored = useMemo(() => readStoredCredentials(), [])
  const learner: { memberId?: string; createdAt?: unknown; customFields?: any } | null = credentials?.memberId
    ? credentials
    : stored
  const memberId = learner?.memberId || ''
  const [hidden, setHidden] = useState(false)

  const language = toSupportedLanguage(i18n.language)
  const visible =
    !hidden &&
    shouldShowLanguageNotice({
      memberId,
      createdAt: learner?.createdAt,
      nativeLanguage: learner?.customFields?.native_language,
      nativeLanguageSource: learner?.customFields?.native_language_source,
      interfaceLanguage: i18n.language,
      pathname,
      answered: !!languageNoticeAnswer(memberId),
      reviewOverride: reviewLanguageOverride(),
    })

  useEffect(() => {
    if (!visible || !language) return
    const key = `${memberId}:${language}`
    if (shownThisSession.has(key)) return
    shownThisSession.add(key)
    track('language_notice_shown', { language })
  }, [visible, language, memberId])

  if (!visible || !language) return null

  const name = languageNameInSentence(language)

  const answer = (choice: LanguageCode) => {
    setHidden(true)
    answerLanguageNotice(memberId, choice)
  }

  const switchToEnglish = () => {
    track('language_notice_switched_to_english', { from: language })
    answer('en')
  }

  const keep = (via: 'button' | 'close') => {
    track('language_notice_kept', { language, via })
    answer(language)
  }

  return (
    <div className='language-notice' role='region' aria-label={t('languageNotice.label')}>
      <div className='container language-notice__inner'>
        <p className='language-notice__text' aria-live='polite'>
          {t('languageNotice.message', { language: name })}
        </p>
        <div className='language-notice__actions'>
          <button type='button' className='btn btn-primary common_btn language-notice__button' lang='en' onClick={switchToEnglish}>
            {SWITCH_TO_ENGLISH}
          </button>
          <button type='button' className='btn button-default-o language-notice__button' onClick={() => keep('button')}>
            {t('languageNotice.keep', { language: name })}
          </button>
        </div>
        <button type='button' className='language-notice__close' aria-label={t('close')} onClick={() => keep('close')}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
            <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default LanguageNotice
